import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { FORM_MODE, PER_PAGE, defaultPagination } from 'src/constants/main';
import theme, { PALETTE_COLORS } from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { UNAVAILABLE_BRANCHES_FLAG } from 'src/types/branches';
import { DELIVERY_ZONE_TYPE, IDeliveryZone, IZoneTooltipOptions, IZoneValidation } from 'src/types/deliveryManagment';
import { IPagination, LatLng } from 'src/types/main';
import { boundsToPath, circleToPolygon, isPointInBounds } from 'src/utils/formatter';
import { getTranslate, getUnavailable } from 'src/utils/helpers';
import { getCenterOfBounds } from 'src/utils/maps';
import { getRandomIntNumber } from '~/helpers';
import { findSubstring } from 'src/utils/parser';
import MapStore, { DEFAULT_ZOOM } from 'src/view/components/Map/store';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

interface IZonesFilter {
  search?: string;
}

const defaultFilters: IZonesFilter = {
  search: undefined,
};

export const initDeliveryZone = (): IDeliveryZone => ({
  id: 0,
  branch: undefined,
  external_id: null,
  status: 1,
  bounds: [],
  radius: '3',
  is_geofencing: 1,
  priority: deliveryZonesStore.pagination.data.length + 1,
  type: DELIVERY_ZONE_TYPE.RADIUS,
  color: PALETTE_COLORS[getRandomIntNumber(0, PALETTE_COLORS.length - 1)],
  name: {
    en: `${localeStore.t('zone')} ${deliveryZonesStore.pagination.data.length + 1}`,
    ar: `${localeStore.t('zone')} ${deliveryZonesStore.pagination.data.length + 1}`,
  },

  created_at: '2021-11-01 15:11:44',
  status_in_branch: 1,
  delivery_fee: 0,
  min_order: 0,
  free_delivery: 0,
  max_cash_payment: 0,
  country: undefined,

  city: undefined,
});

class DeliveryZonesStore {
  constructor() {
    makeObservable(this);
  }

  public mapStore = new MapStore();

  @observable public tooltip: {
    center: LatLng;
    options: { label: string; value: string }[];
  } | null = null;
  @action public setTooltip = (center?: LatLng, _options?: IZoneTooltipOptions, reset?: boolean) => {
    if (!center || !_options || reset) {
      this.tooltip = null;
      return;
    }

    const options: { label: string; value: string }[] = [];
    if (getTranslate(_options.zone_name)) options.push({ label: localeStore.t('zone_name'), value: getTranslate(_options.zone_name) });
    if (_options.radius) options.push({ label: localeStore.t('radius'), value: `${_options.radius} ` + localeStore.t('km') });
    if (getTranslate(_options.branch_name)) options.push({ label: localeStore.t('Branch'), value: getTranslate(_options.branch_name) });

    this.tooltip = { center, options };
  };

  /**
   * visibility param for draw area hint modal
   *
   * @memberof DeliveryZonesStore
   */
  @observable public showDrawHint: boolean = false;
  @action public changeDrawHintVisibility = (showDrawHint: boolean) => {
    this.showDrawHint = showDrawHint;
  };

  /**
   * filters for fetchZones request
   *
   * @memberof DeliveryZonesStore
   */
  @observable public filters: IZonesFilter = defaultFilters;
  /**
   * set filters for request
   *
   * @param {ZonesFilter} filters
   * @memberof DeliveryZonesStore
   */
  @action public setFilters = (filters: IZonesFilter, clear?: boolean) => {
    if (clear) {
      this.filters = defaultFilters;
      return;
    }

    // mapZoom
    if (
      filters.search?.trim() &&
      !(
        !this.zone ||
        findSubstring(getTranslate(this.zone.name), filters.search || '') ||
        findSubstring(getTranslate(this.zone.branch?.name || ''), filters.search || '') ||
        findSubstring(this.zone.external_id || '', filters.search || '')
      )
    ) {
      this.selectZone(null);
    } else {
      this.setDefaultZoom();
    }

    this.filters = { ...this.filters, ...filters };
  };

  /**
   * list of loaded pages, required for pagination and lazy loading starting not from the first page
   *
   * @type {number[]}
   * @memberof OrdersStore
   */
  @observable public pages: number[] = [];
  /**
   * pagination list with delivery zones
   *
   * @type {IPagination<IDeliveryZone>}
   * @memberof DeliveryZonesStore
   */
  @observable pagination: IPagination<IDeliveryZone> = defaultPagination;
  /**
   * active draggable zone
   *
   * @type {(IDeliveryZone | null)}
   * @memberof DeliveryZonesStore
   */
  @observable draggableZone: IDeliveryZone | null = null;

  @observable validation: IZoneValidation = {
    name: false,
    nameErrorMessage: undefined,
    branch: false,
    radius: false,
  };

  @observable lastUpdate: number = 0;

  @action setValidation = (clear?: boolean) => {
    if (!this.draft) return;

    // name validation
    if (getTranslate(this.draft.name).trim()) {
      if (clear) this.validation.nameErrorMessage = undefined;
      else {
        this.validation.nameErrorMessage = this.pagination.data.find(
          (zone) => zone.id !== this.draft?.id && getTranslate(zone.name).trim() === getTranslate(this.draft?.name).trim()
        )
          ? 'zone_name_update_error'
          : undefined;
      }
    }
    this.validation.name = clear ? false : Boolean(this.validation.nameErrorMessage) || !getTranslate(this.draft.name).trim();
    // branch validation
    this.validation.branch = clear ? false : !this.draft.branch;
    // radius validation
    if (this.draft.type === DELIVERY_ZONE_TYPE.PLYGON || clear) {
      this.validation.radius = false;
    } else {
      this.validation.radius = this.draft.radius?.toString() === '0' ? false : !this.draft.radius;
    }
  };

  public setDefaultZoom = () => {
    if (!this.zone) this.setTooltip();

    // update tooltip
    if (this.zone) {
      if (this.zone.type === DELIVERY_ZONE_TYPE.RADIUS) {
        if (!this.zone.branch) this.setTooltip();
        else this.setTooltip(this.getBranchCoordinats(this.zone.branch), { zone_name: this.zone.name, branch_name: this.zone.branch?.name });
      } else if (this.zone.type === DELIVERY_ZONE_TYPE.PLYGON) {
        if (!this.zone.bounds.length) this.setTooltip();
        else this.setTooltip(getCenterOfBounds(this.zone.bounds), { zone_name: this.zone.name, branch_name: this.zone.branch?.name });
      }
    }

    const availableZones: IDeliveryZone[] = this.pagination.data.filter(
      (zone) => zone.status && zone.branch && !getUnavailable(zone.branch, UNAVAILABLE_BRANCHES_FLAG.DELETE)
    );

    if (!availableZones.length) this.mapStore.setZoom(DEFAULT_ZOOM);
    this.mapStore.fitMap(
      availableZones.reduce<[number, number][]>(
        (bounds: [number, number][], zone: IDeliveryZone) => [
          ...bounds,
          ...(zone.type === DELIVERY_ZONE_TYPE.PLYGON ? zone.bounds : this.getPoint(zone)),
        ],
        []
      )
    );
  };

  @action fetchZones = async (page: number = this.pagination.current_page + 1) => {
    try {
      // Data cashing if zones fetched less than 10 min
      if (this.lastUpdate && this.pagination.data.length && moment().diff(moment(this.lastUpdate), 'minutes') < 10) {
        return;
      } else {
        this.pagination = defaultPagination;
        page = 1;
      }

      this.pagination.loading = true;
      this.pagination.last_page = this.pagination.last_page || 1;

      // Fetching all zones in parts
      do {
        const response: IPagination<IDeliveryZone> = await client.get('/v3/areas', {
          params: { search: this.filters.search, page, per_page: PER_PAGE.LARGE },
        });

        response.data = response.data.map(
          (zone: IDeliveryZone): IDeliveryZone => ({
            ...zone,
            type: DELIVERY_ZONE_TYPE[zone.type] ? zone.type : DELIVERY_ZONE_TYPE.PLYGON,
            radius: zone.radius?.toString() || '3',
            color: zone.color || PALETTE_COLORS[getRandomIntNumber(0, PALETTE_COLORS.length - 1)],
          })
        );
        let data = [];

        if (
          (this.filters.search && response.current_page === 1) ||
          (response.current_page === 1 && !this.pages.length) ||
          (response.current_page <= 1 && !response.data.length) ||
          !this.pagination.current_page
        ) {
          this.pages = [];
          data = response.data;
        } else {
          data = [...this.pagination.data, ...response.data].sort((a, b) => a.priority - b.priority);
        }

        this.pages = [...this.pages, response.current_page];
        this.pagination = {
          ...response,
          loading: true,
          refreshing: false,
          data,
        };

        page++;
      } while (page <= this.pagination.last_page);

      this.pagination.loading = false;
      this.lastUpdate = Date.now();
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'DeliveryZonesStore fetchZones' });
    }
  };

  private getStatusParameter = (zone: IDeliveryZone): number => {
    if (!zone.branch) return 0;
    if (zone.type === DELIVERY_ZONE_TYPE.RADIUS && !zone.radius) return 0;
    if (zone.type === DELIVERY_ZONE_TYPE.PLYGON && !zone.bounds.length) return 0;
    return zone.status;
  };

  @action createZone = async (draft: IDeliveryZone) => {
    try {
      if (!draft.branch) return;

      const data = {
        name: !draft.name.en || !draft.name.ar ? undefined : draft.name,
        status: this.getStatusParameter(draft),
        branch_id: draft.branch.id,
        status_in_branch: draft.branch ? 1 : 0,
        is_geofencing: 1,
        bounds: boundsToPath(draft.bounds),
        radius: draft.type === DELIVERY_ZONE_TYPE.PLYGON ? undefined : Number(draft.radius) || 0, // in km
        type: draft.type, // 0 - Circle, 1 - Polygon
        priority: draft.priority,
        color: draft.color, // hex
        external_id: draft.external_id ? draft.external_id.toString() : '',
      };
      if (!data.external_id) delete data.external_id;

      const response: IDeliveryZone = await client.post(`/v3/areas`, data);
      response.radius = response.radius?.toString() || '3';

      this.pagination.data = [...this.pagination.data, response];
      this.clearDraft();

      toastSuccess({ message: localeStore.t('toast_delivery_zone_changes_saved') });
    } catch (error: any) {
      logger.error({ error, place: 'DeliveryZonesStore createZone', showModal: true });
    }
  };

  @action updateZone = async (zone: IDeliveryZone) => {
    try {
      if (!zone.branch) return;

      const data = {
        name: !zone.name.en || !zone.name.ar ? undefined : zone.name,
        // city_id: Number,
        // city_external_id?: Number | String,
        status: this.getStatusParameter(zone),
        branch_id: zone.branch?.id,
        status_in_branch: zone.branch ? 1 : 0,
        is_geofencing: 1,
        bounds: zone.bounds,
        radius: zone.type === DELIVERY_ZONE_TYPE.PLYGON ? undefined : Number(zone.radius) || 0, // in km
        type: zone.type, // 0 - Circle, 1 - Polygon
        priority: zone.priority,
        color: zone.color, // hex
        external_id: zone.external_id ? zone.external_id.toString() : '',
      };
      // if (!data.external_id) delete data.external_id;

      const response: IDeliveryZone = await client.put(`/v3/areas/${zone.id}`, data);
      this.pagination.data = this.pagination.data.map((_zone) => (_zone.id === response.id ? response : _zone));
      this.clearDraft();
      this.selectZone(response);

      toastSuccess({ message: localeStore.t('toast_delivery_zone_changes_saved') });
    } catch (error: any) {
      logger.error({ error, place: 'DeliveryZonesStore updateZone', showModal: true });
    }
  };

  @action deleteZone = async (zone: IDeliveryZone) => {
    try {
      await client.delete(`/v3/areas/${zone.id}`);
      this.pagination.data = this.pagination.data.reduce(
        (sum: IDeliveryZone[], current: IDeliveryZone) =>
          sum.concat(current.priority !== zone.priority ? this.zonePriorityUpdate(current, zone.priority) : []),
        []
      );
      this.selectZone(null);
      // this.changeBranchNotCoveredHintVisibility(false);
      this.changeDrawHintVisibility(false);
    } catch (error: any) {
      logger.error({ error, place: 'DeliveryZonesStore deleteZone', showModal: true });
    }
  };

  private zonePriorityUpdate = (zone: IDeliveryZone, priority: number): IDeliveryZone => {
    if (zone.priority < priority) return zone;
    else return { ...zone, priority: zone.priority - 1 };
  };

  // ZONE

  @observable zone: null | IDeliveryZone = null;

  @observable draft: null | IDeliveryZone = null;
  @observable mode: FORM_MODE = FORM_MODE.HIDDEN;

  @action selectDraft = (zone: IDeliveryZone | null, mode: FORM_MODE) => {
    this.mode = mode;
    this.draft = zone || initDeliveryZone();
    // if (this.draft?.type === DELIVERY_ZONE_TYPE.PLYGON && this.draft.bounds.length) {
    //   this.checkBranchCoverage();
    // }
  };
  @action clearDraft = () => {
    this.mode = FORM_MODE.HIDDEN;
    this.draft = null;
    // this.changeBranchNotCoveredHintVisibility(false);
    this.changeDrawHintVisibility(false);
  };
  @action editDraftZone = (key: any, value: IDeliveryZone[keyof IDeliveryZone]) => {
    if (this.draft) {
      this.draft = {
        ...this.draft,
        [key]: value,
      };
    }
  };

  @action selectZone = (zone: IDeliveryZone | null) => {
    if (!zone) {
      this.zone = null;
      this.setDefaultZoom();
      return;
    }

    this.zone = zone;

    if (!zone.branch && !zone.bounds.length) {
      this.setDefaultZoom();
      return;
    }

    if (zone.type === DELIVERY_ZONE_TYPE.PLYGON) {
      if (zone.bounds.length) {
        const center = getCenterOfBounds(zone.bounds);
        if (zone.branch) {
          this.setTooltip(
            this.branchCoverage(zone) ? this.getBranchCoordinats(zone.branch) : center,
            { zone_name: zone.name, branch_name: zone.branch?.name },
            zone.branch && getUnavailable(zone.branch, UNAVAILABLE_BRANCHES_FLAG.DELETE)
          );
        } else {
          this.setTooltip(center, { zone_name: zone.name });
        }

        // WARNNG !!!
        // If tooltip is outside polygon, rendering tooltip breaks map center detection, so center detection needs to be delayed
        if (!this.branchCoverage(zone)) {
          setTimeout(() => this.mapStore.fitMap(zone.bounds), 50);
        } else {
          this.mapStore.fitMap(zone.bounds);
        }
      } else if (zone.branch) {
        const point = this.getPoint(zone);
        const center = getCenterOfBounds(point);
        this.mapStore.fitMap(point, this.getBranchCoordinats(zone.branch));
        this.setTooltip(this.getBranchCoordinats(zone.branch) || center, { zone_name: zone.name, branch_name: zone.branch?.name });
      } else {
        this.setDefaultZoom();
      }
    } else if (zone.type === DELIVERY_ZONE_TYPE.RADIUS) {
      if (!zone.branch || !zone.radius) {
        this.setDefaultZoom();
        return;
      }
      const point = this.getPoint(zone);
      this.mapStore.fitMap(point, this.getBranchCoordinats(zone.branch));
      this.setTooltip(this.getBranchCoordinats(zone.branch), { zone_name: zone.name, radius: Number(zone.radius), branch_name: zone.branch?.name });
    }
  };

  public getBranchCoordinats = (branch: IDeliveryZone['branch']): LatLng => {
    return { lat: branch?.latitude || 0, lng: branch?.longitude || 0 };
  };

  @action getPoint = (zone: IDeliveryZone) => {
    return circleToPolygon(
      {
        lat: zone.branch?.latitude || 0,
        lng: zone.branch?.longitude || 0,
      },
      Number(zone.radius) || 3,
      1
    );
  };

  public clear = () => {
    this.selectZone(null);
    this.mapStore.clear();
    this.clearDraft();
    this.setFilters({}, true);
  };

  @action updateZonesPriority = async (activeId: number, overId: number) => {
    try {
      if (!this.draggableZone) return;
      this.pagination.loading = true;
      const priority: number = this.pagination.data.find((zone) => zone.id === overId)?.priority || this.draggableZone?.priority;
      this.pagination.data = this.zoneTransform(activeId, overId);
      await client.put(`v3/areas/${activeId}/priority`, { priority });
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      this.pagination.data = this.zoneTransform(overId, activeId);

      logger.error({ error, place: 'DeliveryZonesStore updateZonesPriority', showModal: true });
    }
  };

  private zoneTransform = (activeId: number, overId: number): IDeliveryZone[] => {
    const activeZonePriority: number | null = this.pagination.data.find((zone) => zone.id === activeId)?.priority || null;
    const overZonePriority: number | null = this.pagination.data.find((zone) => zone.id === overId)?.priority || null;
    if (!activeZonePriority || !overZonePriority) return this.pagination.data;

    const editZone = (zone: IDeliveryZone) => {
      if (zone.id === activeId) {
        return { ...zone, priority: overZonePriority };
      } else return { ...zone, priority: activeZonePriority };
    };

    return this.pagination.data
      .map((zone) => (zone.id === activeId || zone.id === overId ? editZone(zone) : zone))
      .sort((a, b) => a.priority - b.priority);
  };

  /**
   * set active draggable zone
   *
   * @param {(number | null)} id
   * @memberof DeliveryZonesStore
   */
  @action setDraggableZone = (id: number | null) => {
    this.draggableZone = this.pagination.data.slice().find((item) => item.id === id) || null;
  };

  // /**
  //  * visibility param for branch not covered hint modal
  //  *
  //  * @memberof DeliveryZonesStore
  //  */
  // @observable public showBranchNotCoveredHint: boolean = false;
  // @action public changeBranchNotCoveredHintVisibility = (state: boolean) => {
  //   this.showBranchNotCoveredHint = state;
  // };
  // @action public checkBranchCoverage = () => {
  //   if (!this.draft || !this.draft.branch) return;

  //   if (!this.branchCoverage(this.draft)) {
  //     this.changeBranchNotCoveredHintVisibility(true);
  //   }
  // };
  public branchCoverage = (zone: IDeliveryZone): boolean => {
    if (!zone.branch || !zone.bounds.length) return false;
    return !Boolean(!isPointInBounds(zone.bounds, this.getBranchCoordinats(zone.branch)));
  };

  public getColor = (zone: IDeliveryZone): string => {
    if (!zone.branch || getUnavailable(zone.branch, UNAVAILABLE_BRANCHES_FLAG.DELETE) || !zone.status) return theme.colors.gray2;
    else return zone.color;
  };
}

const deliveryZonesStore = new DeliveryZonesStore();

export default deliveryZonesStore;
